import React, { useState, useEffect } from 'react';
import { BookOpen, PlayCircle, CheckCircle, Award, TrendingUp, Users, Euro, Shield, Loader2, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const Academy = () => {
  const [completedModules, setCompletedModules] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [certificateIssued, setCertificateIssued] = useState(false);

  const modules = [
    {
      id: 'intro',
      title: 'Introduction au Programme',
      icon: <BookOpen className="h-8 w-8" />,
      duration: '10 min',
      description: 'Comprendre le programme vendeur IPTV4KFR',
      content: [
        { type: 'text', value: 'Bienvenue dans le programme vendeur IPTV4KFR !' },
        { type: 'text', value: 'En tant que vendeur, vous gagnez des commissions sur chaque vente réalisée via votre lien unique.' },
        { type: 'list', title: 'Vos avantages :', items: [
          'Commissions évolutives de 10% à 20%',
          'Tableau de bord de suivi en temps réel',
          'Paiements rapides via PayPal',
          'Programme de paliers (Bronze → Diamant)'
        ]},
        { type: 'text', value: 'Votre réussite dépend de votre capacité à présenter notre offre IPTV premium de manière professionnelle et transparente.' }
      ]
    },
    {
      id: 'selling',
      title: 'Techniques de Vente',
      icon: <TrendingUp className="h-8 w-8" />,
      duration: '20 min',
      description: 'Vendre efficacement le service IPTV',
      content: [
        { type: 'text', value: 'Comment présenter IPTV4KFR de manière convaincante' },
        { type: 'list', title: 'Arguments clés :', items: [
          'Service 100% légal et sécurisé',
          'Plus de 10 000 chaînes en 4K',
          'Prix unique 59€/an (moins de 5€/mois)',
          'Support 24/7 et installation simple',
          'Compatible tous appareils'
        ]},
        { type: 'list', title: 'À éviter absolument :', items: [
          'Promettre des gains excessifs',
          'Forcer la vente ou harceler',
          'Mentir sur les caractéristiques',
          'Négliger le service après-vente'
        ]},
        { type: 'text', value: 'Concentrez-vous sur la valeur réelle : qualité, prix, légalité.' }
      ]
    },
    {
      id: 'retention',
      title: 'Fidélisation Client',
      icon: <Users className="h-8 w-8" />,
      duration: '15 min',
      description: 'Maximiser les renouvellements',
      content: [
        { type: 'text', value: 'Les renouvellements génèrent autant de commission que les nouvelles ventes !' },
        { type: 'list', title: 'Stratégies de fidélisation :', items: [
          'Restez en contact régulier avec vos clients',
          'Rappelez les dates de renouvellement',
          'Proposez de l\'aide pour toute question technique',
          'Informez des nouvelles chaînes ou fonctionnalités'
        ]},
        { type: 'text', value: 'Un client satisfait renouvelle et recommande - double gain pour vous !' }
      ]
    },
    {
      id: 'dashboard',
      title: 'Utiliser le Dashboard',
      icon: <Euro className="h-8 w-8" />,
      duration: '12 min',
      description: 'Lire et optimiser vos statistiques',
      content: [
        { type: 'text', value: 'Votre dashboard vendeur est votre meilleur allié' },
        { type: 'list', title: 'Indicateurs à surveiller :', items: [
          'Solde disponible vs en attente',
          'Nombre de ventes du mois en cours',
          'Palier de commission actuel',
          'Historique des transactions'
        ]},
        { type: 'text', value: 'Suivez vos performances mensuellement et ajustez votre stratégie pour atteindre les paliers supérieurs.' }
      ]
    },
    {
      id: 'compliance',
      title: 'Règles et Conformité',
      icon: <Shield className="h-8 w-8" />,
      duration: '15 min',
      description: 'Respecter les CGU et réglementations',
      content: [
        { type: 'text', value: 'Le respect des règles garantit la pérennité du programme' },
        { type: 'list', title: 'Règles essentielles :', items: [
          'Aucune promesse de gains garantis',
          'Transparence totale sur le service IPTV',
          'Respect de la vie privée des clients',
          'Pas de spam ou démarchage agressif',
          'Déclaration fiscale de vos revenus'
        ]},
        { type: 'text', value: 'Tout manquement peut entraîner la suspension de votre compte vendeur.' }
      ]
    }
  ];

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await api.get('/vendors/academy-progress');
      setCompletedModules(response.data.completed_modules || []);
      setCertificateIssued(response.data.certificate_issued || false);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    setActiveModule(activeModule === moduleId ? null : moduleId);
  };

  const markAsCompleted = async (moduleId) => {
    if (completedModules.includes(moduleId)) return;
    
    setCompleting(moduleId);
    try {
      await api.post(`/vendors/academy-progress/${moduleId}`);
      setCompletedModules([...completedModules, moduleId]);
      
      // Check if all modules completed
      if (completedModules.length + 1 === modules.length) {
        setCertificateIssued(true);
      }
    } catch (error) {
      console.error('Error completing module:', error);
    } finally {
      setCompleting(null);
    }
  };

  const resetProgress = async () => {
    if (!window.confirm('Voulez-vous vraiment réinitialiser votre progression ?')) return;
    
    try {
      await api.delete('/vendors/academy-progress/reset');
      setCompletedModules([]);
      setCertificateIssued(false);
    } catch (error) {
      console.error('Error resetting progress:', error);
    }
  };

  const renderContent = (content) => {
    return content.map((item, index) => {
      if (item.type === 'text') {
        return <p key={index} className="text-[#E8E8E8] mb-4">{item.value}</p>;
      }
      if (item.type === 'list') {
        return (
          <div key={index} className="mb-4">
            {item.title && <p className="text-white font-semibold mb-2">{item.title}</p>}
            <ul className="space-y-2">
              {item.items.map((listItem, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-[#00FFFF] flex-shrink-0 mt-0.5" />
                  <span className="text-[#B8B8BA]">{listItem}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      }
      return null;
    });
  };

  const progress = (completedModules.length / modules.length) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-12 w-12 text-[#00FFFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="academy-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-lg metallic-border cyber-glow"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Award className="h-10 w-10 text-[#00FFFF]" />
            <div>
              <h1 className="text-4xl font-bold text-white">IPTV4KFR Academy</h1>
              <p className="text-[#B8B8BA]">Formation complète pour vendeurs performants</p>
            </div>
          </div>
          {completedModules.length > 0 && (
            <button
              onClick={resetProgress}
              className="text-[#6B6B6E] hover:text-[#B8B8BA] flex items-center space-x-1 text-sm"
              title="Réinitialiser"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Réinitialiser</span>
            </button>
          )}
        </div>

        <div className="bg-[#222226] p-6 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#E8E8E8] font-semibold">Votre progression</span>
            <span className="text-[#00FFFF] font-bold">
              {completedModules.length}/{modules.length} modules complétés
            </span>
          </div>
          <div className="w-full bg-[#3A3A3D] rounded-full h-3 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
              className="h-full bg-gradient-to-r from-[#00FFFF] to-[#00FF88]"
            />
          </div>
          {progress === 100 && (
            <p className="text-[#00FF88] text-sm mt-2 flex items-center">
              <CheckCircle className="h-4 w-4 mr-1" />
              Formation terminée ! Certificat disponible ci-dessous.
            </p>
          )}
        </div>
      </motion.div>

      <div className="space-y-4" data-testid="modules-list">
        {modules.map((module, index) => (
          <motion.div
            key={module.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-[#1A1A1D] rounded-lg metallic-border overflow-hidden"
            data-testid={`module-${module.id}`}
          >
            <div
              className="p-6 cursor-pointer hover:bg-[#222226] transition-colors"
              onClick={() => toggleModule(module.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {completedModules.includes(module.id) ? (
                    <div className="h-12 w-12 bg-[#00FF88]/10 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-7 w-7 text-[#00FF88]" />
                    </div>
                  ) : (
                    <div className="h-12 w-12 bg-[#00FFFF]/10 rounded-full flex items-center justify-center text-[#00FFFF]">
                      {module.icon}
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">{module.title}</h3>
                    <p className="text-[#B8B8BA] text-sm">{module.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-[#B8B8BA] text-sm">{module.duration}</span>
                  {completedModules.includes(module.id) ? (
                    <span className="text-[#00FF88] text-sm font-medium">Terminé</span>
                  ) : (
                    <PlayCircle className="h-6 w-6 text-[#00FFFF]" />
                  )}
                </div>
              </div>
            </div>

            {activeModule === module.id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-[#3A3A3D]"
              >
                <div className="p-8 bg-[#121214]">
                  {renderContent(module.content)}
                  
                  {!completedModules.includes(module.id) && (
                    <button
                      onClick={() => markAsCompleted(module.id)}
                      disabled={completing === module.id}
                      className="btn-primary cyber-glow-hover mt-6 disabled:opacity-50"
                      data-testid={`complete-module-${module.id}`}
                    >
                      {completing === module.id ? (
                        <>
                          <Loader2 className="inline-block mr-2 h-5 w-5 animate-spin" />
                          Enregistrement...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="inline-block mr-2 h-5 w-5" />
                          Marquer comme terminé
                        </>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border">
        <h3 className="text-2xl font-bold text-white mb-4">Certificat de Formation</h3>
        <p className="text-[#B8B8BA] mb-6">
          Complétez tous les modules pour obtenir votre certificat de vendeur IPTV4KFR certifié.
        </p>
        {certificateIssued || progress === 100 ? (
          <div className="bg-gradient-to-r from-[#00FFFF] to-[#00FF88] p-8 rounded-lg text-center">
            <Award className="h-16 w-16 text-[#0A0A0B] mx-auto mb-4" />
            <h4 className="text-2xl font-bold text-[#0A0A0B] mb-2">Félicitations !</h4>
            <p className="text-[#0A0A0B]">Vous êtes maintenant un vendeur IPTV4KFR certifié</p>
            <button className="mt-6 bg-[#0A0A0B] text-[#00FFFF] px-8 py-3 rounded-lg font-bold hover:bg-[#1A1A1D] transition-colors">
              Télécharger le certificat PDF
            </button>
          </div>
        ) : (
          <div className="text-center py-8 bg-[#222226] rounded-lg">
            <p className="text-[#B8B8BA]">
              Complétez encore <span className="text-[#00FFFF] font-bold">{modules.length - completedModules.length}</span> module(s) pour débloquer votre certificat
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Academy;
